import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Archive, ArrowRight, CalendarDays, GripVertical, Inbox, List, MoreHorizontal, Plus, Search } from 'lucide-react';
import { useSession } from '../session-context';
import { LoadingRows, PageTitle, WorkPageError } from './page-states';
import { useOverlays } from './overlay-context';
import InitiativeCalendar from './InitiativeCalendar';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { apiFetch, v1Path } from '../../lib/api';
import { formatDateOnly } from '../../lib/dates';
import {
  archiveInitiative,
  deleteInitiative,
  deleteInitiativeMessage,
  initiativePath,
  INITIATIVES_PATH,
  linkedTaskCount,
  unarchiveInitiative,
} from '../../lib/initiatives';
import { cn } from '../../lib/utils';
import { useLaunchStepsByInitiative } from './use-launch-steps';
import { useProjectDecorations } from './use-project-decorations';
import { AssigneesCell } from './table-cells';
import type { ListResponse, MemberDto, StateDto, WorkItemListDecorations, WorkItemSlim } from '../../lib/work-types';

type InitiativeRow = WorkItemSlim & WorkItemListDecorations;

type Layout = 'list' | 'calendar' | 'backlog' | 'archived';

const LAYOUTS: Array<{ value: Layout; label: string; Icon: typeof List }> = [
  { value: 'list', label: 'List', Icon: List },
  { value: 'calendar', label: 'Calendar', Icon: CalendarDays },
  { value: 'backlog', label: 'Backlog', Icon: Inbox },
  { value: 'archived', label: 'Archived', Icon: Archive },
];

const isLayout = (value: string | null): value is Layout => LAYOUTS.some((layout) => layout.value === value);

function RowMenu({
  initiative,
  archived,
  disabled,
  onArchive,
  onUnarchive,
  onDelete,
}: {
  initiative: InitiativeRow;
  archived: boolean;
  disabled: boolean;
  onArchive: () => void;
  onUnarchive: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          disabled={disabled}
          aria-label={`More actions for ${initiative.key}`}
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {archived ? (
          <DropdownMenuItem onSelect={onUnarchive}>Unarchive</DropdownMenuItem>
        ) : (
          <DropdownMenuItem onSelect={onArchive}>Archive</DropdownMenuItem>
        )}
        <DropdownMenuItem className="text-destructive" onSelect={onDelete}>
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LaunchDate({ dueDate }: { dueDate: string | null }) {
  return (
    <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <CalendarDays className="h-3.5 w-3.5" aria-hidden />
      {dueDate ? (formatDateOnly(dueDate) ?? dueDate) : 'No launch date'}
    </p>
  );
}

function InitiativeRowView({
  initiative,
  rank,
  canDrag,
  members,
  action,
}: {
  initiative: InitiativeRow;
  rank: number;
  canDrag: boolean;
  members: MemberDto[];
  action?: ReactNode;
}) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: initiative.id,
    disabled: !canDrag,
  });
  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn('cursor-pointer border-t hover:bg-muted/40', isDragging && 'relative z-10 bg-muted opacity-80')}
      onClick={() => navigate(initiativePath(initiative.id))}
    >
      <td className="w-14 py-2.5 pl-1.5 pr-0">
        <div className="flex items-center gap-0.5">
          {canDrag ? (
            <button
              type="button"
              className="flex h-7 w-6 cursor-grab touch-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground active:cursor-grabbing"
              aria-label={`Reorder ${initiative.title}`}
              onClick={(event) => event.stopPropagation()}
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4" aria-hidden />
            </button>
          ) : null}
          <span className={cn('text-xs tabular-nums text-muted-foreground', !canDrag && 'pl-1.5')}>{rank}</span>
        </div>
      </td>
      <td className="px-3 py-2.5">
        <Link
          to={initiativePath(initiative.id)}
          className="flex min-w-0 items-baseline gap-2 no-underline hover:no-underline"
          onClick={(event) => event.stopPropagation()}
        >
          <span className="shrink-0 font-mono text-xs text-muted-foreground">{initiative.key}</span>
          <span className="truncate font-medium text-foreground">{initiative.title}</span>
        </Link>
      </td>
      <td className="px-3 py-2.5">
        <AssigneesCell assigneeIds={initiative.assignee_ids} members={members} />
      </td>
      <td className="whitespace-nowrap px-3 py-2.5">
        <LaunchDate dueDate={initiative.due_date} />
      </td>
      {action !== undefined ? (
        <td className="whitespace-nowrap px-3 py-2.5 text-right" onClick={(event) => event.stopPropagation()}>
          {action}
        </td>
      ) : null}
    </tr>
  );
}

function InitiativeTable({
  initiatives,
  rankOf,
  canDrag,
  members,
  onReorder,
  actionFor,
}: {
  initiatives: InitiativeRow[];
  rankOf: (id: string) => number;
  canDrag: boolean;
  members: MemberDto[];
  onReorder: (activeId: string, overId: string) => void;
  actionFor?: (initiative: InitiativeRow) => ReactNode;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) onReorder(String(active.id), String(over.id));
  }
  return (
    <div className="overflow-x-auto rounded-lg border">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <table className="w-full min-w-[48rem] text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr>
              <th className={cn('w-14 py-2 pr-0 font-medium', canDrag ? 'pl-8' : 'pl-3')}>#</th>
              <th className="px-3 py-2 font-medium">Initiative</th>
              <th className="w-36 px-3 py-2 font-medium">Owners</th>
              <th className="px-3 py-2 font-medium">Launch</th>
              {actionFor ? <th className="w-1 px-3 py-2" aria-label="Actions" /> : null}
            </tr>
          </thead>
          <SortableContext items={initiatives.map((row) => row.id)} strategy={verticalListSortingStrategy}>
            <tbody>
              {initiatives.map((initiative) => (
                <InitiativeRowView
                  key={initiative.id}
                  initiative={initiative}
                  rank={rankOf(initiative.id)}
                  canDrag={canDrag}
                  members={members}
                  action={actionFor ? actionFor(initiative) : undefined}
                />
              ))}
            </tbody>
          </SortableContext>
        </table>
      </DndContext>
    </div>
  );
}

export default function InitiativeBoard({
  projectId,
  states,
  error: outerError,
}: {
  /** The hidden Initiatives workspace. */
  projectId: string | undefined;
  states: StateDto[];
  error?: string | null;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast, confirm } = useOverlays();
  const { isOrgAdmin, isAdmin: isSystemAdmin } = useSession();
  const [initiatives, setInitiatives] = useState<InitiativeRow[]>([]);
  const [archivedRows, setArchivedRows] = useState<InitiativeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  const { members } = useProjectDecorations(projectId);
  const query = searchParams.get('q') ?? '';
  const layoutParam = searchParams.get('layout');
  const layout: Layout = isLayout(layoutParam) ? layoutParam : 'list';
  const backlogStateIds = useMemo(
    () => new Set(states.filter((state) => state.group === 'backlog').map((state) => state.id)),
    [states],
  );
  const startState = states.find((state) => state.group === 'unstarted');

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const load = useCallback(async () => {
    if (!projectId) return;
    setError(null);
    try {
      const query = 'types=initiative&show_sub_items=false&order_by=sort_order&limit=100&offset=0';
      const [list, archivedList] = await Promise.all([
        apiFetch<ListResponse<InitiativeRow>>(`${v1Path(`/projects/${projectId}/work-items`)}?${query}`),
        apiFetch<ListResponse<InitiativeRow>>(`${v1Path(`/projects/${projectId}/archives`)}?${query}`),
      ]);
      setInitiatives(list.items);
      setArchivedRows(archivedList.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load initiatives');
      setInitiatives([]);
      setArchivedRows([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    const onChanged = () => void load();
    window.addEventListener('bb:work-items-changed', onChanged);
    return () => window.removeEventListener('bb:work-items-changed', onChanged);
  }, [load]);

  const active = useMemo(() => initiatives.filter((row) => !backlogStateIds.has(row.state_id)), [initiatives, backlogStateIds]);
  const backlog = useMemo(() => initiatives.filter((row) => backlogStateIds.has(row.state_id)), [initiatives, backlogStateIds]);
  const matches = useCallback(
    (rows: InitiativeRow[]) => {
      const needle = query.trim().toLowerCase();
      if (!needle) return rows;
      return rows.filter((initiative) =>
        `${initiative.key} ${initiative.title} ${initiative.summary ?? ''}`.toLowerCase().includes(needle),
      );
    },
    [query],
  );
  const visibleActive = useMemo(() => matches(active), [matches, active]);
  const visibleBacklog = useMemo(() => matches(backlog), [matches, backlog]);
  const visibleArchived = useMemo(() => matches(archivedRows), [matches, archivedRows]);
  const rankById = useMemo(
    () => new Map([...active, ...backlog].map((row) => [row.id, (backlogStateIds.has(row.state_id) ? backlog : active).indexOf(row) + 1])),
    [active, backlog, backlogStateIds],
  );
  const canRank = isOrgAdmin && !query.trim();

  const onReorder = useCallback(
    async (section: InitiativeRow[], activeId: string, overId: string) => {
      const from = section.findIndex((row) => row.id === activeId);
      const to = section.findIndex((row) => row.id === overId);
      if (from < 0 || to < 0) return;
      const next = arrayMove(section, from, to);
      setInitiatives((all) => [...next, ...all.filter((row) => !next.some((moved) => moved.id === row.id))]);
      try {
        await apiFetch(v1Path(`/initiatives/${activeId}/rank`), {
          method: 'POST',
          body: JSON.stringify({ before_id: next[to - 1]?.id ?? null, after_id: next[to + 1]?.id ?? null }),
        });
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Could not reorder initiatives');
        void load();
      }
    },
    [load, toast],
  );

  async function moveToList(initiative: InitiativeRow) {
    if (!projectId || !startState) return;
    setMoving(initiative.id);
    try {
      await apiFetch(v1Path(`/projects/${projectId}/work-items/${initiative.id}`), {
        method: 'PATCH',
        body: JSON.stringify({ version: initiative.version, state_id: startState.id }),
      });
      toast(`Moved ${initiative.key} to Initiatives.`);
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not move the initiative');
      void load();
    } finally {
      setMoving(null);
    }
  }

  async function runRowAction(initiative: InitiativeRow, action: 'archive' | 'unarchive' | 'delete') {
    if (!projectId) return;
    if (action === 'archive') {
      const ok = await confirm({
        title: 'Archive initiative',
        description: `Archive ${initiative.key}? It leaves the Initiatives list and becomes read-only. You can restore it from the Archived view.`,
        confirmLabel: 'Archive',
      });
      if (!ok) return;
    }
    if (action === 'delete') {
      const ok = await confirm({
        title: 'Delete initiative',
        description: deleteInitiativeMessage(initiative.key, linkedTaskCount(initiative.initiative_rollup)),
        confirmLabel: 'Delete',
        destructive: true,
      });
      if (!ok) return;
    }
    setMoving(initiative.id);
    try {
      if (action === 'archive') await archiveInitiative(projectId, initiative);
      else if (action === 'unarchive') await unarchiveInitiative(projectId, initiative);
      else await deleteInitiative(projectId, initiative.id);
      toast(
        action === 'archive'
          ? `Archived ${initiative.key}`
          : action === 'unarchive'
            ? `Restored ${initiative.key}`
            : `Deleted ${initiative.key}`,
      );
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'That did not work');
      void load();
    } finally {
      setMoving(null);
    }
  }

  const rowMenu = (initiative: InitiativeRow, archived: boolean) => (
    <RowMenu
      initiative={initiative}
      archived={archived}
      disabled={moving !== null}
      onArchive={() => void runRowAction(initiative, 'archive')}
      onUnarchive={() => void runRowAction(initiative, 'unarchive')}
      onDelete={() => void runRowAction(initiative, 'delete')}
    />
  );

  const stepsByInitiative = useLaunchStepsByInitiative(
    visibleActive.map((row) => row.id),
    layout === 'calendar',
  );

  const empty = (text: string) => (
    <p className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">{text}</p>
  );
  const ready = !loading && states.length > 0 && !error && !outerError;

  return (
    <div className="flex flex-1 flex-col gap-5">
      <PageTitle
        title="Initiatives"
        action={
          isSystemAdmin ? (
            <Button type="button" onClick={() => navigate(`${INITIATIVES_PATH}/new`)}>
              <Plus className="h-4 w-4" aria-hidden />
              New initiative
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input
            value={query}
            onChange={(event) => setParam('q', event.target.value || null)}
            placeholder="Search initiatives..."
            aria-label="Search initiatives"
            className="h-9 pl-8"
          />
        </div>

        {query ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-9 text-muted-foreground"
            onClick={() => setParam('q', null)}
          >
            Clear all
          </Button>
        ) : null}

        <div className="ml-auto inline-flex items-center rounded-lg border p-0.5" role="group" aria-label="Layout">
          {LAYOUTS.map(({ value, label, Icon }) => (
            <Button
              key={value}
              type="button"
              variant="ghost"
              size="sm"
              aria-pressed={layout === value}
              className={cn(
                'h-8 gap-1.5 px-2.5',
                layout === value ? 'bg-muted text-foreground' : 'text-muted-foreground',
              )}
              onClick={() => setParam('layout', value === 'list' ? null : value)}
            >
              <Icon className="h-4 w-4" aria-hidden />
              {label}
              {(value === 'backlog' || value === 'archived') && ready ? (
                <span className="rounded-full bg-muted px-1.5 text-xs tabular-nums text-muted-foreground">
                  {value === 'backlog' ? backlog.length : archivedRows.length}
                </span>
              ) : null}
            </Button>
          ))}
        </div>
      </div>

      <WorkPageError error={outerError ?? error} projectId={projectId} onJoined={() => void load()} />
      {(loading || states.length === 0) && !outerError ? <LoadingRows /> : null}

      {ready ? (
        layout === 'calendar' ? (
          <InitiativeCalendar
            initiatives={visibleActive.map((row) => ({
              id: row.id,
              key: row.key,
              title: row.title,
              due_date: row.due_date,
              to: initiativePath(row.id),
            }))}
            steps={stepsByInitiative}
          />
        ) : layout === 'backlog' ? (
          visibleBacklog.length === 0 ? (
            empty(query ? 'No backlog initiatives match these filters.' : 'The backlog is empty. New initiatives land here.')
          ) : (
            <InitiativeTable
              initiatives={visibleBacklog}
              rankOf={(id) => rankById.get(id) ?? 0}
              canDrag={canRank}
              members={members}
              onReorder={(activeId, overId) => void onReorder(backlog, activeId, overId)}
              actionFor={
                isOrgAdmin
                  ? (initiative) => (
                      <div className="flex items-center justify-end gap-1">
                        {startState ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1.5"
                            disabled={moving !== null}
                            onClick={() => void moveToList(initiative)}
                          >
                            Move to Initiatives
                            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                          </Button>
                        ) : null}
                        {rowMenu(initiative, false)}
                      </div>
                    )
                  : undefined
              }
            />
          )
        ) : layout === 'archived' ? (
          visibleArchived.length === 0 ? (
            empty(query ? 'No archived initiatives match these filters.' : 'Nothing is archived.')
          ) : (
            <InitiativeTable
              initiatives={visibleArchived}
              rankOf={(id) => visibleArchived.findIndex((row) => row.id === id) + 1}
              canDrag={false}
              members={members}
              onReorder={() => undefined}
              actionFor={isOrgAdmin ? (initiative) => rowMenu(initiative, true) : undefined}
            />
          )
        ) : visibleActive.length === 0 ? (
          empty(query ? 'No initiatives match these filters.' : 'No initiatives yet. Move one up from the Backlog view.')
        ) : (
          <InitiativeTable
            initiatives={visibleActive}
            rankOf={(id) => rankById.get(id) ?? 0}
            canDrag={canRank}
            members={members}
            onReorder={(activeId, overId) => void onReorder(active, activeId, overId)}
            actionFor={isOrgAdmin ? (initiative) => rowMenu(initiative, false) : undefined}
          />
        )
      ) : null}

      {ready && isOrgAdmin && query.trim() ? (
        <p className="text-xs text-muted-foreground">Clear the search to reorder initiatives.</p>
      ) : null}
    </div>
  );
}
